import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUploadManager, UploadItem } from "@/contexts/UploadContext";
import { FileListItem } from "@/components/ui/file-list-item";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface UploadStatusContentProps {
  onNavigateToDialog?: (partnerId: string, dialogType: UploadItem['dialogType']) => void;
}

export const UploadStatusContent = ({ onNavigateToDialog }: UploadStatusContentProps) => {
  const { uploads, removeUpload, retryUpload } = useUploadManager();

  const activeUploads = uploads.filter(u => u.status === 'uploading');
  const failedUploads = uploads.filter(u => u.status === 'error');
  const hasUploads = uploads.length > 0;

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'logo': return 'Logo';
      case 'identity': return 'Identitätsdokumente';
      case 'kooperation': return 'Kooperationsdokumente';
      case 'sonstiges': return 'Sonstige Dokumente';
      case 'antrag': return 'Antragsdokumente';
      case 'ausweisdokumente': return 'Ausweisdokumente';
      case 'beratungsdokumente': return 'Beratungsdokumente';
      case 'polizze': return 'Polizzen';
      case 'angebot_marktvergleich': return 'Angebote/Marktvergleiche';
      case 'auskunftsvollmacht': return 'Auskunftsvollmacht';
      case 'prozessvollmacht': return 'Prozessvollmacht';
      case 'attachment': return 'Anhänge';
      case 'belege': return 'Belege';
      case 'dokumente': return 'Dokumente';
      case 'wissensdatenbank': return 'Wissensdatenbank';
      case 'pdf': return 'PDF-Dokument';
      default: return category;
    }
  };

  if (!hasUploads) {
    return (
      <>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload-Status
          </SheetTitle>
        </SheetHeader>
        <div className="flex items-center justify-center h-40 text-muted-foreground">
          Keine aktiven Uploads
        </div>
      </>
    );
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload-Status
        </SheetTitle>
      </SheetHeader>

      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-4 mt-4">
          {/* Fehlgeschlagene Uploads */}
          {failedUploads.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-destructive flex items-center gap-2">
                Fehlgeschlagen
                <Badge variant="destructive" className="text-xs">
                  {failedUploads.length}
                </Badge>
              </h4>
              {failedUploads.map((upload) => (
                <div key={upload.id} className="space-y-1">
                  <FileListItem
                    fileName={upload.fileName}
                    uploadItem={upload}
                    onRemove={() => removeUpload(upload.id)}
                    onRetry={() => retryUpload(upload.id)}
                    canView={false}
                    canDownload={false}
                  />
                  <div className="text-xs text-muted-foreground pl-2">
                    {upload.partnerName && <div>Partner: {upload.partnerName}</div>}
                    <div>Kategorie: {getCategoryLabel(upload.category)}</div>
                    {onNavigateToDialog && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => onNavigateToDialog(upload.partnerId, upload.dialogType)}
                      >
                        Zum Dialog öffnen →
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Aktive Uploads */}
          {activeUploads.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                Wird hochgeladen
                <Badge variant="secondary" className="text-xs">
                  {activeUploads.length}
                </Badge>
              </h4>
              {activeUploads.map((upload) => (
                <div key={upload.id} className="space-y-1">
                  <FileListItem
                    fileName={upload.fileName}
                    uploadItem={upload}
                    onRemove={() => removeUpload(upload.id)}
                    canView={false}
                    canDownload={false}
                  />
                  <div className="text-xs text-muted-foreground pl-2">
                    {upload.partnerName && <div>Partner: {upload.partnerName}</div>}
                    <div>Kategorie: {getCategoryLabel(upload.category)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Erfolgreiche Uploads */}
          {uploads.filter(u => u.status === 'success').length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-chart-5 flex items-center gap-2">
                Erfolgreich
                <Badge variant="secondary" className="text-xs bg-chart-5/10 text-chart-5">
                  {uploads.filter(u => u.status === 'success').length}
                </Badge>
              </h4>
              {uploads.filter(u => u.status === 'success').map((upload) => (
                <FileListItem
                  key={upload.id}
                  fileName={upload.fileName}
                  url={upload.url}
                  uploadItem={upload}
                  onRemove={() => removeUpload(upload.id)}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </>
  );
};
